import styled from "styled-components";
import { Button } from "../../styles/components/Buttons.styled";
import { StyledPopupButton } from "../../styles/components/Popup.styled";

// Lists-specific styles only. Shared primitives (Button) stay shared.

export const ListsPageWrapper = styled.div`
  max-width: 760px;
  margin: 0 auto;
  padding: 10px 14px 24px;
  text-align: left;
`;

export const ListsNotice = styled.div`
  background-color: #fff;
  border: 2px solid #8dabe2;
  border-radius: 12px;
  color: #608cdf;
  padding: 16px;
  margin: 10px 0;
`;

export const ErrorBanner = styled.div`
  align-items: center;
  background-color: #ffe8e8;
  border: 2px solid #e06060;
  border-radius: 12px;
  color: #a32222;
  display: flex;
  gap: 10px;
  justify-content: space-between;
  margin: 10px 0;
  padding: 10px 14px;
`;

export const DismissButton = styled.button`
  background: none;
  border: none;
  color: #a32222;
  cursor: pointer;
  font-size: 18px;
  font-weight: 700;
  line-height: 1;
  padding: 0 4px;
`;

export const SelectRow = styled.div`
  align-items: center;
  display: flex;
  flex: 1 1 260px;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;
`;

// Holds the trigger and the menu that opens beneath it, so the menu can be positioned
// against the trigger and an outside press can be told apart from one inside.
export const SelectAnchor = styled.div`
  flex: 1 1 200px;
  min-width: 0;
  position: relative;
`;

// The bordered box carrying both lines. A button, so the whole box opens the menu.
export const SelectShell = styled.button`
  background-color: #fff;
  border: 2px solid #8dabe2;
  border-radius: 10px;
  box-sizing: border-box;
  color: inherit;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  font: inherit;
  gap: 2px;
  margin: 0;
  padding: 6px 10px 7px;
  position: relative;
  text-align: left;
  width: 100%;
  &:focus-visible {
    outline: 2px solid #608cdf;
  }
`;

export const ListMenu = styled.ul`
  background-color: #fff;
  border: 2px solid #608cdf;
  border-radius: 10px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.18);
  box-sizing: border-box;
  left: 0;
  list-style: none;
  margin: 4px 0 0;
  max-height: 320px;
  overflow-y: auto;
  padding: 4px;
  position: absolute;
  right: 0;
  top: 100%;
  z-index: 20;
  &:focus {
    outline: none;
  }
`;

// $active is the keyboard/hover highlight; the check mark shows the list actually chosen.
export const ListOption = styled.li<{ $active: boolean }>`
  align-items: center;
  background-color: ${({ $active }) => ($active ? "#e8f0fd" : "transparent")};
  border-radius: 7px;
  cursor: pointer;
  display: flex;
  gap: 8px;
  padding: 8px 10px;
`;

export const OptionCheck = styled.span`
  color: #608cdf;
  display: flex;
  flex: 0 0 16px;
  font-size: 16px;
`;

export const ListOptionText = styled.span`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
`;

export const ListOptionName = styled.span<{ $selected: boolean }>`
  color: #608cdf;
  font-size: 16px;
  font-weight: ${({ $selected }) => ($selected ? 700 : 600)};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// A span, since it sits inside the trigger button.
export const SelectedName = styled.span`
  color: #608cdf;
  display: block;
  font-size: 18px;
  font-weight: 700;
  overflow: hidden;
  padding-right: 20px;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const SelectChevron = styled.span`
  align-items: center;
  color: #608cdf;
  display: flex;
  font-size: 14px;
  pointer-events: none;
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
`;

// No overflow: hidden — the list menu hangs out of the top section and would be clipped
// on a short list. The sections carry no background, so the rounded corners hold anyway.
export const PanelCard = styled.div`
  background-color: #fff;
  border: 2px solid #8dabe2;
  border-radius: 12px;
`;

// Sections share the card. The thick rule between them matches the card border, so the
// three read as divisions of one thing rather than three separate things.
export const PanelSection = styled.div`
  padding: 14px;
  & + & {
    border-top: 2px solid #8dabe2;
  }
`;

export const PanelHeader = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: space-between;
`;

export const DoneCount = styled.span`
  color: #7a8aa3;
  font-size: 14px;
  font-weight: 600;
`;

export const PanelActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

export const SmallButton = styled(Button)`
  font-size: 14px;
  height: 28px;
  margin: 0;
  width: auto;
  min-width: 70px;
  padding: 0 12px;
`;

export const TextInput = styled.input`
  border: 2px solid #8dabe2;
  border-radius: 10px;
  color: #33475b;
  /* 16px keeps iOS Safari from zooming the page when the field takes focus. */
  font-size: 16px;
  padding: 7px 10px;
  &:focus {
    outline: 2px solid #608cdf;
  }
`;

export const ItemTextInput = styled(TextInput)`
  flex: 3 1 180px;
  min-width: 0;
`;

export const QuantityInput = styled(TextInput)`
  flex: 1 1 90px;
  min-width: 0;
`;

export const ItemList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

export const ItemRow = styled.li<{ $dragging?: boolean }>`
  align-items: flex-start;
  border-bottom: 1px solid #edf2fa;
  /* The whole row is the drag target, so it advertises that with the cursor. While a
     row is being dragged its floating copy carries the content, and what stays behind
     is only a gap — hence the near-invisible original. */
  cursor: grab;
  display: flex;
  gap: 10px;
  opacity: ${({ $dragging }) => ($dragging ? 0.4 : 1)};
  padding: 10px 2px;
  &:active {
    cursor: grabbing;
  }
`;

// Pills replace the row checkbox. A pill carries its own label, so it can say what it
// means rather than relying on a column header, and a row of them wraps instead of
// eating the item's text width — which is what makes more than one of them workable on
// a phone. Filled means ticked, outlined means not.
export const StagePills = styled.div<{ $spaced?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: ${({ $spaced }) => ($spaced ? "6px" : "0")};
`;

export const StagePill = styled.button<{ $on?: boolean; $mixed?: boolean }>`
  background-color: ${({ $on }) => ($on ? "#608cdf" : "#fff")};
  border: 2px solid #8dabe2;
  border-radius: 15px;
  color: ${({ $on }) => ($on ? "#fff" : "#33475b")};
  cursor: pointer;
  font-size: 13px;
  font-weight: 700;
  /* Shorter than the 44px Apple asks for, but the whole pill is the target and it
     matches the 36px icon buttons already in the row. */
  min-height: 30px;
  padding: 4px 12px;
  /* A part-ticked list reads as neither on nor off, so the fill only half lands. */
  ${({ $mixed }) =>
    $mixed
      ? `background-image: linear-gradient(90deg, #608cdf 50%, #fff 50%);
         color: #33475b;`
      : ""}
  &:focus-visible {
    outline: 2px solid #608cdf;
    outline-offset: 1px;
  }
`;

export const ItemBody = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

export const ItemText = styled.div<{ $checked?: boolean }>`
  color: ${({ $checked }) => ($checked ? "#9aa7b8" : "#33475b")};
  font-size: 16px;
  overflow-wrap: break-word;
  text-decoration: ${({ $checked }) => ($checked ? "line-through" : "none")};
`;

export const QuantityBadge = styled.span`
  background-color: #e8f0fd;
  border-radius: 10px;
  color: #608cdf;
  font-size: 13px;
  font-weight: 700;
  margin-left: 8px;
  padding: 2px 8px;
  white-space: nowrap;
`;

export const ItemNote = styled.div`
  color: #7a8aa3;
  font-size: 13px;
  margin-top: 3px;
  overflow-wrap: break-word;
`;

export const RowActions = styled.div`
  display: flex;
  flex: 0 0 auto;
  gap: 4px;
`;

export const EditFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const EmptyState = styled.div`
  color: #7a8aa3;
  padding: 16px 2px;
`;

// Route shell. The shared .route-wrapper class assumes a 60px header, but the real
// header is taller than that, which pushed the footer just past the fold. ListsRoute
// measures the space above it instead and passes the result in as $minHeight.
export const ListsRouteWrapper = styled.div<{ $minHeight?: number }>`
  display: flex;
  flex-direction: column;
  min-height: ${({ $minHeight }) =>
    $minHeight != null ? `${$minHeight}px` : "auto"};
`;

export const ListsRouteContent = styled.div`
  flex: 1 1 auto;
`;

// The add form stacks into two lines so the note field has room without squeezing
// the text and quantity inputs on a phone.
export const AddItemFields = styled.form`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 8px 0 0;
`;

export const AddItemRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

export const NoteInput = styled(TextInput)`
  flex: 1 1 200px;
  min-width: 0;
`;

// New List modal interior. The surrounding overlay and card come from the app's
// shared Popup.styled primitives, so only the contents are defined here.
export const ModalForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const ModalHeading = styled.h2`
  font-size: 22px;
  margin: 0;
`;

export const ModalInput = styled.input`
  background-color: #fff;
  border: 2px solid #608cdf;
  border-radius: 10px;
  box-sizing: border-box;
  color: #33475b;
  /* 16px keeps iOS Safari from zooming the page when the field takes focus. */
  font-size: 16px;
  padding: 9px 10px;
  width: 100%;
`;

// Square icon-only buttons for the list's own rename and delete actions. Not built on
// the shared Button, whose fixed 140px width and pill radius fight a square icon target.
export const IconButton = styled.button`
  align-items: center;
  background-color: #fff;
  border: 2px solid #8dabe2;
  border-radius: 10px;
  color: #608cdf;
  cursor: pointer;
  display: flex;
  flex: 0 0 auto;
  font-size: 16px;
  height: 36px;
  justify-content: center;
  padding: 0;
  width: 36px;
  &:hover:not(:focus) {
    opacity: 0.9;
    transform: scale(0.96);
  }
  &:focus {
    outline: 2px solid #608cdf;
  }
`;

export const DeleteIconButton = styled(IconButton)`
  border-color: #e0a0a0;
  color: #a32222;
  &:focus {
    outline: 2px solid #a32222;
  }
`;

// Confirmation dialog interior.
export const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const ModalText = styled.div`
  font-size: 17px;
  line-height: 1.5;
`;

export const ModalDangerButton = styled(StyledPopupButton)`
  background-color: #a32222;
  color: #fff;
`;

// Disclosure toggle for the add-item form. Reads as a heading rather than a button,
// since its job is to label the section it opens.
export const DisclosureButton = styled.button`
  align-items: center;
  background: none;
  border: none;
  color: #608cdf;
  cursor: pointer;
  display: flex;
  font-size: 15px;
  font-weight: 700;
  gap: 6px;
  margin: 0;
  padding: 0;
  text-align: left;
  &:hover:not(:focus) {
    opacity: 0.8;
  }
`;

// Labelled field inside the inline item editor. The labels matter more here than in the
// add form: an item being edited already has values, so a placeholder would be hidden.
export const EditField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

export const EditLabel = styled.label`
  color: #608cdf;
  font-size: 13px;
  font-weight: 700;
`;

// The floating copy of the row that follows the pointer. A ul so the row it holds is
// still a list item inside a list.
export const DragOverlayCard = styled.ul`
  background-color: #fff;
  border: 2px solid #608cdf;
  border-radius: 10px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.25);
  cursor: grabbing;
  list-style: none;
  margin: 0;
  padding: 0 8px;
  li {
    border-bottom: none;
  }
`;

// Row of whole-list actions, sitting just above the items.
export const ToolbarRow = styled.div`
  align-items: center;
  border-bottom: 1px solid #cddcf4;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding-bottom: 8px;
`;

// Caption for the whole-list pill beside it. The pill is the control now, so the
// caption is no longer itself clickable.
export const SelectAllLabel = styled.div`
  align-items: center;
  color: #608cdf;
  display: flex;
  font-size: 14px;
  font-weight: 700;
  gap: 8px;
`;

// Offline status strips. Amber for "not on the server yet", green for "it is now",
// matching the colours the inventory sync badges already use.
export const PendingNotice = styled.div`
  background-color: #fef3c7;
  border: 2px solid #f59e0b;
  border-radius: 12px;
  color: #7c4a03;
  font-size: 14px;
  font-weight: 600;
  margin: 10px 0;
  padding: 10px 14px;
`;

export const SyncedNotice = styled.div`
  background-color: #dcfce7;
  border: 2px solid #16a34a;
  border-radius: 12px;
  color: #145c2e;
  font-size: 14px;
  font-weight: 600;
  margin: 10px 0;
  padding: 10px 14px;
`;
